import { Injectable, Inject, inject } from '@angular/core';
import { ACLService } from '@delon/acl';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { _HttpClient, SettingsService } from '@delon/theme';
import { environment } from '@env/environment';
import { shellEmbedEncodeDeflatedJson } from './shell-embed-codec';

type ReportingHostEnvironment = {
  /** Explicit `true` or string `"true"` enables satellite preview; anything else → in-app. */
  useReportingHostPreview?: boolean | string;
  /** Blank or missing → {@link ReportingService.DEFAULT_REPORTING_SHELL_ORIGIN}. */
  reportingShellOrigin?: string;
};

@Injectable({ providedIn: 'root' })
export class ReportingService {
  private reportViewerUrl: string = '#/reporting/viewer/preview/dx';

  private static readonly DEFAULT_REPORTING_SHELL_ORIGIN =
    'https://reporting.qne.cloud';

  /**
   * Must match {@link SHELL_EMBED_QUERY_PARAM} in `angular-report-designer` `shell-embed-config.ts`.
   * Readable JSON keys, then raw DEFLATE + base64 with `z:` prefix (see `shell-embed-codec.ts`).
   */
  private static readonly SHELL_EMBED_QUERY_PARAM = 'config';

  private encodeReportingShellEmbed(payload: {
    accountApiBase: string;
    webAppOrigin: string;
    /** Full reporting API base URL (replaces legacy embed `host` + `port` or `viewerHost`). */
    reportingApiBase?: string;
    reportingMirror?: string;
    aiHost?: string;
    tenantId?: string | number | null;
    isSample: boolean;
    isFreeTrial: boolean;
    accountHost?: string;
    /** Derived flags so the satellite shell can skip `GET /api/User/MyAbilities` when both are sent. */
    canDesign: boolean;
    canAi: boolean;
  }): string {
    const o: Record<string, unknown> = {
      accountApiBase: payload.accountApiBase,
      webAppOrigin: payload.webAppOrigin,
      isSample: payload.isSample,
      isFreeTrial: payload.isFreeTrial
    };
    if (payload.reportingMirror) {
      o['reportingMirror'] = payload.reportingMirror;
    }
    if (payload.aiHost) {
      o['aiHost'] = payload.aiHost;
    }
    if (payload.tenantId != null && String(payload.tenantId).trim() !== '') {
      o['tenantId'] = String(payload.tenantId).trim();
    }
    if (payload.accountHost?.trim()) {
      o['accountHost'] = payload.accountHost.trim();
    }
    if (payload.reportingApiBase?.trim()) {
      o['reportingApiBase'] = payload.reportingApiBase.trim().replace(/\/$/, '');
    }
    o['canDesign'] = payload.canDesign;
    o['canAi'] = payload.canAi;
    return shellEmbedEncodeDeflatedJson(o);
  }

  constructor(
    private http: _HttpClient,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService
  ) {}

  get mirrorServerUrl(): string {
    let serverUrl = environment.api['reporting_mirror'];
    // remove ending slash
    if (serverUrl.endsWith('/')) serverUrl = serverUrl.slice(0, -1);
    return serverUrl;
  }

  get reportServerUrl(): string {
    let serverUrl = environment.api['reporting'];
    // remove ending slash
    if (serverUrl.endsWith('/')) serverUrl = serverUrl.slice(0, -1);
    return serverUrl;
  }

  get reportServerMirrorUrl(): string {
    let serverUrl = environment.api['reporting_mirror'];

    if (serverUrl) {
      // remove ending slash
      if (serverUrl.endsWith('/')) serverUrl = serverUrl.slice(0, -1);
      return serverUrl;
    }

    // If the mirror URL is not set, use the main URL
    return this.reportServerUrl;
  }

  private reportingHostEnv(): ReportingHostEnvironment {
    return environment as ReportingHostEnvironment;
  }

  /**
   * Satellite preview is on only when explicitly true (or string "true").
   * Undefined, null, false, "", whitespace, or any other value → in-app viewer.
   */
  private reportingHostPreviewEnabled(): boolean {
    const v = this.reportingHostEnv().useReportingHostPreview;
    if (v === true) {
      return true;
    }
    if (typeof v === 'string' && v.trim().toLowerCase() === 'true') {
      return true;
    }
    return false;
  }

  /** Non-blank origin for satellite app URLs; blank/missing uses {@link DEFAULT_REPORTING_SHELL_ORIGIN}. */
  private resolvedReportingShellOrigin(): string {
    const raw = this.reportingHostEnv().reportingShellOrigin;
    if (raw == null) {
      return ReportingService.DEFAULT_REPORTING_SHELL_ORIGIN.replace(/\/$/, '');
    }
    const trimmed = String(raw).trim();
    const base =
      trimmed === ''
        ? ReportingService.DEFAULT_REPORTING_SHELL_ORIGIN
        : trimmed;
    return base.replace(/\/$/, '');
  }

  /** Same rules as the in-app report preview `canDesign` getter. */
  private computeCanDesignForShellEmbed(reportType: string | undefined): boolean {
    if (!reportType) {
      return false;
    }
    const acl = this.aclService;
    const hasBasePermission = acl.canAbility({
      ability: [
        'report.update',
        'reportfile.update',
        `${reportType}.design`
      ],
      mode: 'oneOf'
    });
    if (!hasBasePermission) {
      return false;
    }
    if (acl.canAbility('country.PH')) {
      return (
        acl.canAbility(`${reportType}.design`) &&
        acl.canAbility('m1.65536')
      );
    }
    if (acl.canAbility('country.MY')) {
      return true;
    }
    return false;
  }

  private computeCanAiForShellEmbed(): boolean {
    return this.aclService.canAbility('ai.enabled');
  }

  private buildShellEmbedPayload(
    reportingApiBase: string,
    reportType: string | undefined
  ): {
    accountApiBase: string;
    webAppOrigin: string;
    accountHost: string;
    reportingApiBase: string;
    isSample: boolean;
    isFreeTrial: boolean;
    tenantId?: string;
    reportingMirror?: string;
    aiHost?: string;
    canDesign: boolean;
    canAi: boolean;
  } {
    const accountHost = window.location.hostname;
    const isSample = !!this.settingsService.user['company']?.isSample;
    const isFreeTrial = !!this.settingsService.user['company']?.isFreeTrial;
    const tenantId = this.settingsService.user['company']?.tenantId;
    const mirror = this.mirrorServerUrl.replace(/\/$/, '');
    const primary = this.reportServerUrl.replace(/\/$/, '');
    return {
      accountApiBase: this.getAccountApiBaseForShell(),
      webAppOrigin: window.location.origin.replace(/\/$/, ''),
      accountHost,
      reportingApiBase: reportingApiBase.replace(/\/$/, ''),
      isSample,
      isFreeTrial,
      tenantId: tenantId != null ? String(tenantId) : undefined,
      ...(mirror !== primary ? { reportingMirror: mirror } : {}),
      ...(typeof environment.api['ai'] === 'string' &&
      environment.api['ai'].trim() !== ''
        ? { aiHost: environment.api['ai'].replace(/\/$/, '') }
        : {}),
      canDesign: this.computeCanDesignForShellEmbed(reportType),
      canAi: this.computeCanAiForShellEmbed()
    };
  }

  /**
   * Reporting API base URL for the Web Document Viewer `host` option — must match the server
   * that created the task (or primary/mirror rules for static `rf-` reports).
   */
  private getPreviewViewerHostForRf(reportType: string | undefined): string {
    if (reportType && reportType.indexOf('voucher') !== -1) {
      return this.reportServerMirrorUrl.replace(/\/$/, '');
    }
    return this.reportServerUrl.replace(/\/$/, '');
  }

  private getPreviewViewerHostForTask(
    reportType: string | undefined,
    usePrimaryDb: boolean
  ): string {
    if (usePrimaryDb) {
      return this.reportServerMirrorUrl.replace(/\/$/, '');
    }
    if (reportType && reportType.indexOf('voucher') !== -1) {
      return this.reportServerMirrorUrl.replace(/\/$/, '');
    }
    return this.reportServerUrl.replace(/\/$/, '');
  }

  /**
   * Absolute base URL for main accounting API calls from the reporting shell (`accountApiBase`).
   * Resolves relative `environment.api.baseUrl` (e.g. docker-local `/`) against the current origin.
   */
  private getAccountApiBaseForShell(): string {
    const raw = String(environment.api?.baseUrl ?? '/').trim();
    if (raw === '' || raw === '/') {
      return window.location.origin.replace(/\/$/, '');
    }
    const noTrailingSlash = raw.replace(/\/+$/, '');
    if (/^https?:\/\//i.test(noTrailingSlash)) {
      return noTrailingSlash;
    }
    const path = noTrailingSlash.startsWith('/')
      ? noTrailingSlash
      : `/${noTrailingSlash}`;
    return new URL(path, window.location.origin).href.replace(/\/+$/, '');
  }

  /**
   * Satellite **preview** URL only: `config` includes `reportingApiBase` (full reporting URL), `accountHost`, etc.; query is `config` + `token` only.
   * In-app preview (`#/reporting/viewer/preview/...`) is unchanged.
   */
  private buildReportingShellPreviewUrl(
    reportType: string | undefined,
    reportUrl: string,
    viewerHost: string
  ): string {
    const shellOrigin = this.resolvedReportingShellOrigin();

    const token = this.tokenService.get()?.token ?? '';

    const q = new URLSearchParams();
    const embedPayload = this.buildShellEmbedPayload(
      viewerHost.replace(/\/$/, ''),
      reportType
    );
    q.set(
      ReportingService.SHELL_EMBED_QUERY_PARAM,
      this.encodeReportingShellEmbed(embedPayload)
    );
    q.set('token', token);

    const pathTail = reportType
      ? `${encodeURIComponent(reportType)}/${encodeURIComponent(reportUrl)}`
      : `${encodeURIComponent(reportUrl)}`;

    return `${shellOrigin}/preview/${pathTail}?${q.toString()}`;
  }

  design(reportType: string, reportFileUrl: string, filter: any = null) {
    return new Promise((resolve, reject) => {
      var fs = this.toFilterString(filter);
      this.http
        .post(`${this.reportServerUrl}/api/reportTasks/create?_silent=true`, {
          reportUrl: reportFileUrl,
          filter: fs
        })
        .subscribe({
          next: res => {
            if (res) {
              // var url = `${window.location.origin}/#/reporting/viewer/design/${reportType}/rt-${res.taskId}`;
              // window.open(url, '_blank');
              this.openDesigner(reportType, `rt-${res.taskId}`);
            }
            resolve(true);
          },
          error: () => {
            reject(false);
          }
        });
    });
  }

  settingsService = inject(SettingsService);
  private readonly aclService = inject(ACLService);

  /**
   * Opens the satellite report **designer** from the **main webapp** (preview toolbar, report flows, etc.).
   * Uses legacy query parameters (`host`, `accountApiBase`, `tenantId`, …). Satellite **preview** uses
   * compressed `config` (including host/accountHost) via {@link buildReportingShellPreviewUrl}; in-app preview
   * stays on `#/reporting/viewer/preview/...` and is unchanged.
   */
  openDesigner(reportType: string, reportUrl: string) {
    // reporting.qne.cloud uses `host` (+ optional `port`) to reach the reporting API.
    // Relative `reporting` URLs resolve against the current page origin (proxy port applies).
    const reportServerUrl = new URL(
      this.reportServerUrl,
      window.location.origin
    );
    let serverHostname = reportServerUrl.hostname;
    if (serverHostname === '127.0.0.1') {
      serverHostname = 'localhost';
    }
    const isLoopback =
      serverHostname === 'localhost' || serverHostname === '::1';
    const hostAlias = isLoopback ? 'localhost' : serverHostname.split('.')[0];

    // Get token from token service
    const token = this.tokenService.get()?.token;

    // Get accountHost from current application's URL
    const accountHost = window.location.hostname; // e.g., account-test.qne.cloud

    const isSample = this.settingsService.user['company']?.isSample;
    const isFreeTrial = this.settingsService.user['company']?.isFreeTrial;
    const tenantId = this.settingsService.user['company']?.tenantId;

    let url = `${this.resolvedReportingShellOrigin()}/design/${reportType}/${reportUrl}?host=${encodeURIComponent(hostAlias)}`;
    if (isLoopback && reportServerUrl.port) {
      url += `&port=${encodeURIComponent(reportServerUrl.port)}`;
    }
    const accountApiBase = this.getAccountApiBaseForShell();
    url += `&accountHost=${encodeURIComponent(accountHost)}&accountApiBase=${encodeURIComponent(accountApiBase)}&isSample=${!!isSample}&isFreeTrial=${!!isFreeTrial}`;
    if (tenantId != null) {
      url += `&tenantId=${encodeURIComponent(String(tenantId))}`;
    }
    const embedPayload = this.buildShellEmbedPayload(
      this.reportServerUrl.replace(/\/$/, ''),
      reportType
    );
    url += `&${ReportingService.SHELL_EMBED_QUERY_PARAM}=${encodeURIComponent(
      this.encodeReportingShellEmbed(embedPayload)
    )}`;
    url += `&token=${encodeURIComponent(token ?? '')}`;

    window.open(url, '_blank');
  }

  previewCompanyHeader() {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          `${environment.api['reporting']}api/reportfiles/GetDefaultReport?_silent=true`,
          { reportType: 'company-header' }
        )
        .subscribe(res => {
          this.design('company-header', res.reportUrl, null)
            .then(() => {
              resolve(true);
            })
            .catch(() => {
              reject(false);
            });
        });
    });
  }

  previewProjectHeader(projectId: number) {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          `${environment.api['reporting']}api/reportfiles/GetDefaultReport?_silent=true`,
          { reportType: 'company-header' }
        )
        .subscribe(res => {
          this.design('company-header', res.reportUrl, {
            projectId,
            forceProjectHeader: true
          })
            .then(() => {
              resolve(true);
            })
            .catch(() => {
              reject(false);
            });
        });
    });
  }

  previewDefault(
    reportType: string,
    filter: any,
    usePrimaryDb: boolean = false
  ) {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          `${environment.api['reporting']}api/reportfiles/GetDefaultReport?_silent=true`,
          { reportType }
        )
        .subscribe(res => {
          this.preview(reportType, res.reportUrl, filter, usePrimaryDb)
            .then(() => {
              resolve(true);
            })
            .catch(() => {
              reject(false);
            });
        });
    });
  }

  // TODO: Refactor this method to use new generateLink method
  preview(
    docCode: string,
    reportType: string,
    reportFileUrl: string,
    filter: any,
    usePrimaryDb: boolean = false,
    additionalData: any = null
  ) {
    return new Promise((resolve, reject) => {
      if (!reportFileUrl) {
        reject(false);
        return;
      }
      if (reportFileUrl.startsWith('rf-')) {
        if (this.reportingHostPreviewEnabled()) {
          const viewerHost = this.getPreviewViewerHostForRf(reportType);
          const url = this.buildReportingShellPreviewUrl(
            reportType,
            reportFileUrl,
            viewerHost
          );
          window.open(url, '_blank');
        } else {
          var url = `${this.reportViewerUrl}/${reportType}/${reportFileUrl}`;
          window.open(url, '_blank');
        }
        resolve(true);
        return;
      }

      var fs = filter == null ? null : this.toFilterString(filter);

      // When usePrimaryDb is true, use the reportServerMirrorUrl
      // Normally, usePrimaryDb is false, when it is true, means it is previewing from the document header
      let reportServerUrl = usePrimaryDb
        ? this.reportServerMirrorUrl
        : this.reportServerUrl;

      this.http
        .post(`${reportServerUrl}/api/reportTasks/Create?_silent=true`, {
          docCode: docCode,
          reportUrl: reportFileUrl,
          filter: fs,
          usePrimaryDb: usePrimaryDb,
          additionalData: additionalData
        })
        .subscribe({
          next: res => {
            if (res) {
              if (this.reportingHostPreviewEnabled()) {
                const viewerHost = this.getPreviewViewerHostForTask(
                  reportType,
                  usePrimaryDb
                );
                const url = this.buildReportingShellPreviewUrl(
                  reportType,
                  `rt-${res.taskId}`,
                  viewerHost
                );
                window.open(url, '_blank');
              } else {
                var url = `${window.location.origin}/${this.reportViewerUrl}/${reportType}/rt-${res.taskId}`;
                window.open(url, '_blank');
              }
              resolve(true);
              return;
            }
            reject(false);
          },
          error: () => {
            reject(false);
          }
        });
    });
  }

  generateLink(
    reportType: string,
    reportFileUrl: string,
    filter: any,
    usePrimaryDb: boolean = false
  ) {
    return new Promise((resolve, reject) => {
      if (!reportFileUrl) {
        reject(false);
        return;
      }

      if (reportFileUrl.startsWith('rf-')) {
        if (this.reportingHostPreviewEnabled()) {
          const viewerHost = this.getPreviewViewerHostForRf(reportType);
          resolve(
            this.buildReportingShellPreviewUrl(
              reportType,
              reportFileUrl,
              viewerHost
            )
          );
        } else {
          var url = `${this.reportViewerUrl}/${reportType}/${reportFileUrl}`;
          resolve(url);
        }
        return;
      }

      var fs = filter == null ? null : this.toFilterString(filter);

      this.http
        .post(`${this.reportServerUrl}/api/reportTasks/Create?_silent=true`, {
          reportUrl: reportFileUrl,
          filter: fs,
          usePrimaryDb: usePrimaryDb
        })
        .subscribe({
          next: res => {
            if (res) {
              if (this.reportingHostPreviewEnabled()) {
                const viewerHost = this.getPreviewViewerHostForTask(
                  reportType,
                  usePrimaryDb
                );
                resolve(
                  this.buildReportingShellPreviewUrl(
                    reportType,
                    `rt-${res.taskId}`,
                    viewerHost
                  )
                );
              } else {
                var url = `${window.location.origin}/${this.reportViewerUrl}/${reportType}/rt-${res.taskId}`;
                resolve(url);
              }
              return;
            }
            reject(false);
          },
          error: () => {
            reject(false);
          }
        });
    });
  }

  queue(
    reportType: string,
    reportFileUrl: string,
    filter: any,
    usePrimaryDb: boolean = false
  ) {
    return new Promise((resolve, reject) => {
      if (!reportFileUrl) {
        reject(false);
        return;
      }

      if (reportFileUrl.startsWith('rf-')) {
        if (this.reportingHostPreviewEnabled()) {
          const viewerHost = this.getPreviewViewerHostForRf(reportType);
          resolve(
            this.buildReportingShellPreviewUrl(
              reportType,
              reportFileUrl,
              viewerHost
            )
          );
        } else {
          resolve(`${this.reportViewerUrl}/${reportType}/${reportFileUrl}`);
        }
        return;
      }

      var fs = filter == null ? null : this.toFilterString(filter);

      this.http
        .post(`${this.reportServerUrl}/api/reportTasks/Create`, {
          reportUrl: reportFileUrl,
          filter: fs,
          usePrimaryDb: usePrimaryDb,
          queue: true
        })
        .subscribe({
          next: () => {
            // Queued run does not return a preview URL; callers only wait for completion.
            resolve(undefined);
          },
          error: () => {
            resolve(false);
          }
        });
    });
  }

  private toFilterString(data: any) {
    if (!data) return null;
    const js: any = {};
    for (const key in data) {
      if (data[key] instanceof Date && data[key])
        js[key] = this.formatDate(data[key]);
      else js[key] = data[key];
    }
    return JSON.stringify(js);
  }

  private formatDate(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const format = `${year}-${month}-${day}T00:00:00.000Z`;
    return format;
  }
}
